import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Obs, useSubtree } from "subtree.js/react";
import {
  type OnboardingStep,
  onboardingPath,
  onboardingSteps,
  type SellingPath,
  sellingPath,
} from "../../core/primitives/onboarding";
import { ShellActions } from "../../shell/shell-model";

/** Must render in the shell's subtree, not inside a page's own <Subtree>. */
export function LogOutButton() {
  const actions = useSubtree(ShellActions);
  return (
    <Obs>
      {(ref) => {
        const signOut = ref.disableUntilCompleted(actions.signOut);
        return (
          <button
            type="button"
            onClick={signOut}
            disabled={signOut === undefined}
            className="text-[13px] font-medium text-muted transition-colors hover:text-white"
          >
            {signOut === undefined ? "Logging out…" : "Log out"}
          </button>
        );
      }}
    </Obs>
  );
}

/** The way out at the top right of the wizard and the shop picker: on air, below the bar fixed over the page. */
export function TopLogOut() {
  return (
    <div className="absolute top-[calc(max(1rem,env(safe-area-inset-top))+var(--on-air,0px))] right-[max(1rem,env(safe-area-inset-right))] z-10">
      <LogOutButton />
    </div>
  );
}

/** The wizard's steps under their way out; on air both go below the bar, which covers the top of the page. */
export function WizardFrame({ children }: { children: ReactNode }) {
  return (
    <div className="relative pt-[var(--on-air,0px)]">
      <TopLogOut />
      {children}
    </div>
  );
}

/**
 * Done and current both wear the accent — a finished step is filled, the current
 * one is outlined — so the mint traces exactly how far the merchant has got.
 * Upcoming steps stay on the hairline, which is what makes the trail readable at
 * a glance on a near-black ground.
 */
function StepDot({ state, index }: { state: "done" | "current" | "todo"; index: number }) {
  if (state === "done") {
    return (
      <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-accent text-[13px] font-semibold text-ink">
        ✓
      </span>
    );
  }
  return (
    <span
      className={`flex h-[26px] w-[26px] items-center justify-center rounded-full border-[1.5px] text-xs font-semibold ${
        state === "current" ? "border-accent text-accent" : "border-line text-faint"
      }`}
    >
      {index + 1}
    </span>
  );
}

/**
 * The progress indicator sits above the card and is always on screen. Completed
 * steps are links, because a finished step can be revisited; the current and
 * upcoming ones are not.
 */
function StepIndicator({
  steps,
  current,
  storeId,
  claimWalkthrough,
}: {
  steps: { key: OnboardingStep; label: string }[];
  current: OnboardingStep;
  storeId?: string;
  claimWalkthrough: boolean;
}) {
  const currentIndex = steps.findIndex((step) => step.key === current);

  return (
    <ol className="flex items-center">
      {steps.map((step, index) => {
        const state = index < currentIndex ? "done" : index === currentIndex ? "current" : "todo";
        const dot = (
          <>
            <StepDot state={state} index={index} />
            <span
              className={`text-[10px] ${
                state === "current" ? "font-semibold text-accent" : state === "done" ? "text-muted" : "text-faint"
              }`}
            >
              {step.label}
            </span>
          </>
        );

        return (
          <li key={step.key} className="contents">
            {index > 0 && (
              <span
                aria-hidden="true"
                className={`mb-4 h-[1.5px] flex-1 ${index <= currentIndex ? "bg-accent" : "bg-line"}`}
              />
            )}
            {state === "done" ? (
              <Link
                to={onboardingPath(step.key, storeId, claimWalkthrough)}
                className="flex w-11 flex-col items-center gap-1.5 rounded-btn hover:opacity-80"
              >
                {dot}
              </Link>
            ) : (
              <span
                className="flex w-11 flex-col items-center gap-1.5"
                aria-current={state === "current" ? "step" : undefined}
              >
                {dot}
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export function OnboardingLayout({
  step,
  title,
  description,
  children,
  footer,
  storeId,
  claimWalkthrough = false,
}: {
  step: OnboardingStep;
  title: string;
  description: string;
  children: ReactNode;
  footer: ReactNode;
  storeId?: string;
  /** Whether the wizard is reviewing a claimed shop; the page reads it from its state. */
  claimWalkthrough?: boolean;
}) {
  // The path's own step says which way it went, even before the answer is stored.
  const path: SellingPath | null =
    step === "show" ? "shows" : step === "locations" ? "shop" : storeId ? sellingPath(storeId) : null;
  const steps = onboardingSteps(path);
  const stepNumber = steps.findIndex((item) => item.key === step) + 1;

  return (
    <div className="min-h-screen bg-app px-4 py-10 sm:py-14">
      <div className="mx-auto flex w-full max-w-[420px] flex-col gap-[18px]">
        <StepIndicator steps={steps} current={step} storeId={storeId} claimWalkthrough={claimWalkthrough} />

        <div className="panel p-6">
          <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-faint">
            Step {stepNumber} of {steps.length}
          </p>
          <h1 className="text-[19px] font-semibold leading-tight text-white">{title}</h1>
          <p className="mt-1.5 text-pretty text-[13px] leading-relaxed text-muted">{description}</p>

          <div className="mt-5">{children}</div>

          <div className="mt-5">{footer}</div>
        </div>
      </div>
    </div>
  );
}

export function PrimaryButton({ children, className = "", ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button {...props} className={`btn-primary btn-sm ${className}`}>
      {children}
    </button>
  );
}

export function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-[13px] font-medium text-muted transition-colors hover:text-white"
    >
      Back
    </button>
  );
}

export function FormError({ message }: { message?: string | null }) {
  if (!message) return null;
  return <p className="note note-danger mb-4 text-[13px] text-danger">{message}</p>;
}
